import { useField } from 'formik';
import { useState, useCallback } from 'react';

import { CommonFieldProps } from 'types';
import { controlError } from 'utils/error';

import { Input, InputProps } from '../input';

export interface InputFieldProps extends CommonFieldProps, InputProps {
  name: string;
  // Display only: how the value reads (e.g. money as "1189.30"). The form
  // value itself is never changed by it. Once the user types, the field shows
  // exactly what is typed until it loses focus.
  formatValue?: (value: string) => string;
  normalize?: (value: string, prevValue: string) => string | null | undefined;
}

export const InputField = ({
  name,
  label,
  normalize,
  formatValue,
  controlErrorLabel = true,
  ...props
}: InputFieldProps) => {
  const [{ value, onChange, ...fieldProps }, meta, { setValue, setTouched }] =
    useField<string>(name);

  const [typing, setTyping] = useState(false);
  const error = controlError(meta, name, label, controlErrorLabel);
  const change = useCallback(
    async (newValue: string) => {
      await setValue(newValue, true);
    },
    [setValue]
  );

  return (
    <Input
      {...props}
      {...fieldProps}
      label={label}
      error={error}
      value={formatValue && !typing ? formatValue(value ?? '') : (value ?? '')}
      onBlur={(e) => {
        setTyping(false);
        if (e) {
          fieldProps.onBlur(e);
        } else {
          setTouched(true);
        }
      }}
      onChange={async (event) => {
        if (formatValue) {
          setTyping(true);
        }
        if (typeof event === 'string') {
          await change(event);
        } else if (typeof event === 'undefined' && props.type === 'tel') {
          await change('');
        } else {
          if (normalize) {
            await change(normalize(event.target.value, value) || '');
          } else {
            await onChange(event || '');
          }
        }

        props.onChange?.(event);
      }}
    />
  );
};
